<template>
  <div class="v2-address-section">
    <!-- Billing Address -->
    <v2-card>
      <template #header>
        <div class="v2-address-section__header">
          <h3 class="v2-heading-sm">
            Billing Address 1
          </h3>
          <v2-dropdown
            v-if="!addresses.isNotEntityAdmin.value && billingAddrOrDefault._id && !billingAddrOrDefault.isEditEnabled"
            align="right"
          >
            <template #toggle="{ isOpen }">
              <button
                class="v2-address-section__dots-btn"
                :class="{ 'v2-address-section__dots-btn--active': isOpen }"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <circle
                    cx="12"
                    cy="5"
                    r="2"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="2"
                  />
                  <circle
                    cx="12"
                    cy="19"
                    r="2"
                  />
                </svg>
              </button>
            </template>
            <template #menu="{ close }">
              <div class="v2-address-section__menu">
                <button
                  class="v2-address-section__menu-item"
                  @click="onEditBilling(close)"
                >
                  Edit
                </button>
              </div>
            </template>
          </v2-dropdown>
        </div>
      </template>
      <div
        v-if="billingAddrOrDefault.isEditEnabled"
        class="v2-address-section__form-wrap"
      >
        <v2-address-form
          :address="billingAddrOrDefault"
          :countries="countries"
          :disabled="addresses.isNotEntityAdmin.value"
          :show-cancel="!!billingAddrOrDefault._id"
          :show-reset="!billingAddrOrDefault._id"
          @save="onSaveBilling"
          @cancel="onCancelBilling"
        />
      </div>
      <v2-address-detail
        v-else
        :address="billingAddrOrDefault"
      />
    </v2-card>

    <hr class="v2-address-section__divider">

    <!-- Shipping Addresses -->
    <v2-card
      v-for="(addr, idx) in shippingAddrs"
      :key="addr._id || idx"
    >
      <template #header>
        <div class="v2-address-section__header">
          <div class="v2-address-section__header-left">
            <h3 class="v2-heading-sm">
              Shipping Address {{ idx + 1 }}
            </h3>
            <v2-badge
              v-if="addr.isPrimary && shippingAddrs.length > 1"
              variant="primary"
            >
              Primary
            </v2-badge>
          </div>
          <v2-dropdown
            v-if="!addresses.isNotEntityAdmin.value && addr._id && !addr.isEditEnabled"
            align="right"
          >
            <template #toggle="{ isOpen }">
              <button
                class="v2-address-section__dots-btn"
                :class="{ 'v2-address-section__dots-btn--active': isOpen }"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <circle
                    cx="12"
                    cy="5"
                    r="2"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="2"
                  />
                  <circle
                    cx="12"
                    cy="19"
                    r="2"
                  />
                </svg>
              </button>
            </template>
            <template #menu="{ close }">
              <div class="v2-address-section__menu">
                <button
                  class="v2-address-section__menu-item"
                  :class="{ 'v2-address-section__menu-item--disabled': addr.isPrimary || shippingAddrs.length <= 1 }"
                  :disabled="addr.isPrimary || shippingAddrs.length <= 1"
                  @click="onSetPrimary(addr, close)"
                >
                  Set As Primary
                </button>
                <button
                  class="v2-address-section__menu-item"
                  @click="onEditShipping(addr, close)"
                >
                  Edit
                </button>
                <button
                  class="v2-address-section__menu-item v2-address-section__menu-item--danger"
                  :class="{ 'v2-address-section__menu-item--disabled': addr.isPrimary || shippingAddrs.length <= 1 }"
                  :disabled="addr.isPrimary || shippingAddrs.length <= 1"
                  @click="onRemove(addr, close)"
                >
                  Remove
                </button>
              </div>
            </template>
          </v2-dropdown>
        </div>
      </template>

      <v2-address-form
        v-if="addr.isEditEnabled"
        :address="addr"
        :countries="countries"
        :disabled="addresses.isNotEntityAdmin.value"
        :show-cancel="shippingAddrs.length > 1"
        :show-reset="shippingAddrs.length <= 1 && !!addr._id"
        @save="onSaveShipping"
        @cancel="onCancelShipping(addr)"
      />
      <v2-address-detail
        v-else
        :address="addr"
      />
    </v2-card>

    <!-- Empty Shipping State -->
    <v2-card v-if="shippingAddrs.length === 0">
      <template #header>
        <h3 class="v2-heading-sm">
          Shipping Address
        </h3>
      </template>
      <div class="v2-address-section__empty">
        No shipping address found.
      </div>
    </v2-card>

    <!-- Add Address Button -->
    <div
      v-if="canAddAddress && !addresses.isNotEntityAdmin.value"
      class="v2-address-section__add"
    >
      <v2-button
        variant="primary"
        @click="onAddShippingAddress"
      >
        + ADD ADDRESS
      </v2-button>
    </div>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api'
import { apiToastSuccessV2 } from '@/@core/utils/toast'
import V2Card from '@/components/v2/V2Card.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'
import V2AddressForm from './V2AddressForm.vue'
import V2AddressDetail from './V2AddressDetail.vue'

export default {
  name: 'V2AddressSection',
  components: {
    V2Card,
    V2Badge,
    V2Button,
    V2Dropdown,
    V2AddressForm,
    V2AddressDetail,
  },
  props: {
    addresses: {
      type: Object,
      required: true,
    },
    countries: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const billingAddr = computed(() => props.addresses.billingAddress.value[0] || null)
    const billingAddrOrDefault = computed(() => billingAddr.value || props.addresses.newAddress(
      props.addresses.ADDRESSES_TYPE.BILLING,
      true,
    ))
    const shippingAddrs = computed(() => props.addresses.shippingAddress.value)

    const canAddAddress = computed(() => {
      const shipping = shippingAddrs.value
      if (shipping.length === 0) return true
      const last = shipping[shipping.length - 1]
      return !!last._id
    })

    async function onSaveBilling(address, done) {
      address.addressType = props.addresses.ADDRESSES_TYPE.BILLING
      address.isPrimary = true
      await props.addresses.addOrUpdateAddress(address)
      if (done) done()
    }

    function onEditBilling(close) {
      close()
      billingAddrOrDefault.value.isEditEnabled = true
    }

    function onCancelBilling() {
      billingAddrOrDefault.value.isEditEnabled = false
    }

    async function onSaveShipping(address, done) {
      address.addressType = props.addresses.ADDRESSES_TYPE.SHIPPING
      if (shippingAddrs.value.filter(a => a._id).length === 0) {
        address.isPrimary = true
      }
      await props.addresses.addOrUpdateAddress(address)
      if (done) done()
    }

    function onSetPrimary(addr, close) {
      close()
      props.addresses.setAsPrimaryAddress(addr._id).then(() => {
        apiToastSuccessV2('Primary shipping address updated successfully')
      }).catch(() => {})
    }

    function onEditShipping(addr, close) {
      close()
      addr.isEditEnabled = true
    }

    function onCancelShipping(addr) {
      if (!addr._id) {
        const shpIdx = props.addresses.shippingAddress.value.indexOf(addr)
        if (shpIdx !== -1) {
          props.addresses.shippingAddress.value.splice(shpIdx, 1)
        }
      } else {
        addr.isEditEnabled = false
      }
    }

    function onRemove(addr, close) {
      close()
      props.addresses.removeAddress(addr)
    }

    function onAddShippingAddress() {
      const newAddr = props.addresses.newAddress(
        props.addresses.ADDRESSES_TYPE.SHIPPING,
        false,
      )
      props.addresses.shippingAddress.value.push(newAddr)
    }

    return {
      billingAddr,
      billingAddrOrDefault,
      shippingAddrs,
      canAddAddress,
      onSaveBilling,
      onEditBilling,
      onCancelBilling,
      onSaveShipping,
      onSetPrimary,
      onEditShipping,
      onCancelShipping,
      onRemove,
      onAddShippingAddress,
    }
  },
}
</script>

<style lang="scss" scoped>
.v2-address-section {
  display: flex;
  flex-direction: column;
  gap: var(--v2-space-5);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
  }

  &__dots-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    border-radius: var(--v2-radius-md);
    color: var(--v2-muted-foreground);
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover,
    &--active {
      background: var(--v2-muted);
      color: var(--v2-foreground);
    }
  }

  &__menu {
    padding: var(--v2-space-1) 0;
    min-width: 150px;
  }

  &__menu-item {
    display: block;
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    background: transparent;
    border: none;
    text-align: left;
    cursor: pointer;
    transition: background 0.1s ease;

    &:hover {
      background: var(--v2-muted);
    }

    &--danger {
      color: var(--v2-destructive);
    }

    &--disabled {
      opacity: 0.4;
      cursor: not-allowed;

      &:hover {
        background: transparent;
      }
    }
  }

  &__divider {
    border: none;
    border-top: 1px solid var(--v2-border);
    margin: 0;
  }

  &__form-wrap {
    margin-top: var(--v2-space-2);
  }

  &__empty {
    padding: var(--v2-space-4);
    text-align: center;
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
  }

  &__add {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
